import React from 'react';
import { chimneyHoodLayout } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Wall-mount chimney hood — the 2D twin of `ventHoodParts.buildWallChimneyHood`.
 * Every number comes from `chimneyHoodLayout` (proceduralSpecs.js), so the
 * canopy taper, the duct cover and its telescoping seam land exactly where
 * the mesh puts them.
 *
 * The duct cover rises ABOVE the object box (negative y) by the object's
 * own `chimneyHeight`; both views rely on `overflow: visible`, as the box
 * only contains the canopy — the same contract as the floating hood.
 *
 * Paths use ABSOLUTE commands only (M / L / Z) so the sync test can read
 * the drawn extents straight off the `d` strings.
 */

/** Thin uniform architectural line — shared by every vent-hood view. */
export function Stroke({ strokeColor, strokeWidth, ...rest }) {
  return (
    <path
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      {...rest}
    />
  );
}

const GRILLE_TICKS = 8;

/** Closed outline through absolute points. */
const closed = (pts) =>
  `M ${pts[0][0]} ${pts[0][1]} ` +
  pts
    .slice(1)
    .map(([x, y]) => `L ${x} ${y}`)
    .join(' ') +
  ' Z';

/** Open polyline through absolute points. */
const open = (pts) =>
  `M ${pts[0][0]} ${pts[0][1]} ` +
  pts
    .slice(1)
    .map(([x, y]) => `L ${x} ${y}`)
    .join(' ');

/**
 * Duct cover as two stacked open-bottom rectangles: the lower section
 * stands on the canopy top (y = 0, whose edge is already drawn), the
 * inset upper section stands on the lower's top edge (the seam).
 * `x0`/`x1` bound the lower section, `ux0`/`ux1` the upper.
 */
function DuctCover({ x0, x1, ux0, ux1, layout, ...stroke }) {
  const seamY = -layout.lowerHeight;
  const topY = -(layout.lowerHeight + layout.upperHeight);
  return (
    <g>
      <Stroke
        d={open([
          [x0, 0],
          [x0, seamY],
          [x1, seamY],
          [x1, 0],
        ])}
        {...stroke}
      />
      {layout.upperHeight > 0 && (
        <Stroke
          d={open([
            [ux0, seamY],
            [ux0, topY],
            [ux1, topY],
            [ux1, seamY],
          ])}
          {...stroke}
        />
      )}
    </g>
  );
}

/**
 * Front elevation (width × height): the canopy's lip band, the trapezoid
 * of the sloped faces up to the top platform, the grille strip on the lip,
 * and the duct cover centred above.
 */
export function ChimneyHoodFrontView({
  width,
  height,
  depth,
  chimneyHeight,
  ...stroke
}) {
  const layout = chimneyHoodLayout({ width, height, depth, chimneyHeight });
  const lipTopY = height - layout.lip;
  const topX0 = (width - layout.topWidth) / 2;
  const topX1 = topX0 + layout.topWidth;
  const grilleX = (width - layout.grilleWidth) / 2;
  const grilleY = height - layout.lip / 2 - layout.grilleHeight / 2;
  const ductX0 = (width - layout.ductWidth) / 2;
  const upperX0 = (width - layout.upperWidth) / 2;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {/* Canopy silhouette: lip band + sloped sides up to the top platform. */}
      <Stroke
        d={closed([
          [0, lipTopY],
          [0, height],
          [width, height],
          [width, lipTopY],
          [topX1, 0],
          [topX0, 0],
        ])}
        {...stroke}
      />
      {/* Lip crease — where the sloped front meets the vertical band. */}
      <Stroke
        d={open([
          [0, lipTopY],
          [width, lipTopY],
        ])}
        {...stroke}
      />
      {/* Grille strip on the lip, with filter ticks. */}
      <Stroke
        d={closed([
          [grilleX, grilleY],
          [grilleX + layout.grilleWidth, grilleY],
          [grilleX + layout.grilleWidth, grilleY + layout.grilleHeight],
          [grilleX, grilleY + layout.grilleHeight],
        ])}
        {...stroke}
      />
      {Array.from({ length: GRILLE_TICKS }, (_, i) => {
        const x = grilleX + ((i + 1) / (GRILLE_TICKS + 1)) * layout.grilleWidth;
        return (
          <Stroke
            key={i}
            d={open([
              [x, grilleY + layout.grilleHeight * 0.2],
              [x, grilleY + layout.grilleHeight * 0.8],
            ])}
            strokeOpacity={0.6}
            {...stroke}
          />
        );
      })}
      <DuctCover
        x0={ductX0}
        x1={ductX0 + layout.ductWidth}
        ux0={upperX0}
        ux1={upperX0 + layout.upperWidth}
        layout={layout}
        {...stroke}
      />
    </svg>
  );
}

/**
 * Side elevation (depth × height). LEFT view convention: x = 0 is the
 * FRONT (room side), x = depth is the BACK (wall); RIGHT mirrors. The
 * canopy is the mesh's exact cross-section — full depth at the lip,
 * sloping back to the top platform against the wall — and the duct
 * cover's back face sits on the wall line.
 */
export function ChimneyHoodSideView({
  width,
  height,
  depth,
  chimneyHeight,
  view,
  ...stroke
}) {
  const layout = chimneyHoodLayout({ width, height, depth, chimneyHeight });
  const lipTopY = height - layout.lip;

  const profile = (
    <g>
      <Stroke
        d={closed([
          [0, height],
          [depth, height],
          [depth, 0],
          [depth - layout.topDepth, 0],
          [0, lipTopY],
        ])}
        {...stroke}
      />
      <Stroke
        d={open([
          [0, lipTopY],
          [depth, lipTopY],
        ])}
        {...stroke}
      />
      <DuctCover
        x0={depth - layout.ductDepth}
        x1={depth}
        ux0={depth - layout.upperDepth}
        ux1={depth}
        layout={layout}
        {...stroke}
      />
    </g>
  );

  return (
    <svg
      viewBox={`0 0 ${depth} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {view === 'right' ? (
        <g transform={`translate(${depth}, 0) scale(-1, 1)`}>{profile}</g>
      ) : (
        profile
      )}
    </svg>
  );
}
